import React, { useEffect, useState } from 'react';
import { TrendingUpIcon } from './Icons';
import './InsightsWidget.css';
import { API, authFetch } from '../api';
import {
  hasValidInsightsCache,
  loadStoredInsights,
  loadStoredModelStatus,
  storeInsights,
} from '../utils/insightsStore';
import {
  DASHBOARD_INSIGHTS_PREVIEW_LIMIT,
  FALLBACK_HEALTH_INSIGHTS,
} from './healthInsights';

// Five inline SVGs matching the five insight categories on the
// Insights page. Same single-path / 24×24 viewBox convention as the
// activity icon set so everything reads as one family. State of
// Health is a pulse / EKG line — explicitly NOT a heart.
function CategoryGlyph({ category, color, size = 20 }) {
  const c = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none' };
  const stroke = { stroke: color, strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' };

  switch (category) {
    case 'state-of-health':
      return (
        <svg {...c}>
          <path d="M2 12h4l2-6 4 12 4-9 2 3h4" {...stroke} fill="none" />
        </svg>
      );
    case 'processing-of-stress':
      return (
        <svg {...c} fill={color}>
          <path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75C7 8 17 8 17 8z" />
        </svg>
      );
    case 'biological-age':
      return (
        <svg {...c} fill={color}>
          <path d="M12 22a4 4 0 0 1-3.4-6.06A3.99 3.99 0 0 1 6 12a3.99 3.99 0 0 1 2.6-3.94A4 4 0 0 1 12 2a4 4 0 0 1 3.4 6.06A3.99 3.99 0 0 1 18 12a3.99 3.99 0 0 1-2.6 3.94A4 4 0 0 1 12 22zm0-12a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
        </svg>
      );
    case 'performance-potential':
      return (
        <svg {...c} fill={color}>
          <path d="M11 21h-1l1-7H6.5c-.88 0-.33-.75-.31-.78C7.48 10.94 9.41 7.54 12.07 3h1l-1 7h4.51c.4 0 .62.19.4.66C12.97 17.55 11 21 11 21z" />
        </svg>
      );
    case 'burnout-resistance':
      return (
        <svg {...c} fill={color}>
          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 15l-4-4 1.41-1.41L10 13.17l6.59-6.59L18 8l-8 8z" />
        </svg>
      );
    default:
      return <TrendingUpIcon size={size} color={color} />;
  }
}

export default function InsightsWidget() {
  const [aiInsights, setAiInsights] = useState(null);
  const [modelStatus, setModelStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check cache first - only use if status is COMPLETED with actual data
    if (hasValidInsightsCache()) {
      const cachedInsights = loadStoredInsights();
      const cachedStatus = loadStoredModelStatus();
      if (cachedInsights && cachedStatus === 'COMPLETED' && cachedInsights.length > 0) {
        setAiInsights(cachedInsights);
        setModelStatus(cachedStatus);
        setLoading(false);
        console.log('[InsightsWidget] Loaded from cache');
        return;
      }
      console.log('[InsightsWidget] Invalid cache state (', cachedStatus, '), fetching fresh');
    }

    console.log('[InsightsWidget] No valid cache - fetching from API');

    async function initAndFetchModels() {
      try {
        // Step 1: Call init endpoint
        const initResponse = await authFetch(API.modelInit(), {
          method: 'POST',
        });

        if (!initResponse.ok) {
          console.error('Failed to init models:', initResponse.status);
          setModelStatus('FAILED');
          setLoading(false);
          return;
        }

        const initData = await initResponse.json();
        const status = initData.status;
        setModelStatus(status);

        // If models are running/started, we don't fetch results yet
        if (status === 'RUNNING' || status === 'STARTED') {
          setLoading(false);
          return;
        }

        // If FAILED, show error
        if (status === 'FAILED') {
          setLoading(false);
          return;
        }

        // If NOTHING, try to fetch existing cached results
        await fetchModelOutputs();

      } catch (error) {
        console.error('Error in model flow:', error);
        setModelStatus('FAILED');
        setLoading(false);
      }
    }
    
    async function fetchModelOutputs() {
      try {
        const response = await authFetch(API.modelOverview(), {
          method: 'GET',
        });
        if (response.ok) {
          const data = await response.json();
          if (data.hasOutput !== false && data.insights && data.insights.length > 0) {
            setAiInsights(data.insights);
            setModelStatus('COMPLETED');
            // Save to cache only on successful data
            storeInsights(data.insights, 'COMPLETED');
            console.log('[InsightsWidget] Saved to cache');
          } else {
            setModelStatus('NO_DATA');
            // DO NOT cache NO_DATA state
          }
        } else {
          setModelStatus('FAILED');
        }
      } catch (error) {
        console.error('Failed to fetch model outputs:', error);
        setModelStatus('FAILED');
      } finally {
        setLoading(false);
      }
    }
    
    initAndFetchModels();
  }, []);

  // Map AI insights to categories
  // Backend returns: [{text, status, metric}, ...]
  const getInsightsWithAiData = () => {
    // Ensure aiInsights is an array, otherwise use fallback
    if (!Array.isArray(aiInsights) || aiInsights.length < 5) return FALLBACK_HEALTH_INSIGHTS;

    // Map metric names to insight indices
    const metricToIndex = {
      'processingOfStress': 1,
      'burnoutResistance': 4,
      'performance': 3,
      'biologicalAge': 2,
      'healthState': 0,
    };

    return FALLBACK_HEALTH_INSIGHTS.map((insight, idx) => {
      // Find the AI insight that matches this category
      const aiInsight = aiInsights.find(item => metricToIndex[item.metric] === idx);
      if (aiInsight && aiInsight.text) {
        return {
          ...insight,
          body: aiInsight.text,
          preview: aiInsight.text,
          statusWord: aiInsight.status, // Add status word
        };
      }
      return insight;
    });
  };

  const insights = getInsightsWithAiData().slice(0, DASHBOARD_INSIGHTS_PREVIEW_LIMIT);

  // Render different states
  const renderContent = () => {
    if (loading) {
      return <div className="insights-loading">Analyzing...</div>;
    }

    if (modelStatus === 'RUNNING' || modelStatus === 'STARTED') {
      return (
        <div className="insights-widget-processing">
          <span>🔄 Analyzing your data...</span>
        </div>
      );
    }

    if (modelStatus === 'FAILED') {
      return (
        <div className="insights-widget-error">
          <span>Failed to load. Refresh to retry.</span>
        </div>
      );
    }

    if (modelStatus === 'NOTHING' || modelStatus === 'NO_DATA') {
      // Kompakt yatay görünüm: küçük ikon + iki satır metin. Önceki
      // tek-satır varyant kart içinde sağda devasa boşluk bırakıyor,
      // dashboard'ın ritmini bozuyordu.
      return (
        <div
          className="insights-widget-empty"
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 12,
            padding: '14px 4px',
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'rgba(16, 185, 129, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <TrendingUpIcon size={20} color="#10b981" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>
              No measurement data yet
            </span>
            <span style={{ fontSize: 13, opacity: 0.7 }}>
              Complete a 12+ hour measurement to unlock insights.
            </span>
          </div>
        </div>
      );
    }

    return insights.map((insight) => {
      const color = insight.iconColor || (insight.tone === 'purple' ? '#6366f1' : '#10b981');

      return (
        <div key={insight.id} className="insight-mini-item">
          <div
            className="insight-mini-icon"
            style={{ backgroundColor: `${color}15` }}
          >
            <CategoryGlyph category={insight.id} color={color} size={20} />
          </div>

          <div className="insight-mini-info">
            <span className="insight-mini-title">{insight.title}</span>
            <span className="insight-mini-desc">{insight.preview || insight.body}</span>
          </div>

          <div className="insight-mini-meta">
            <div className="insight-metric-group">
              {insight.statusWord && (
                <span className="insight-status-word" style={{ color }}>
                  {insight.statusWord}
                </span>
              )}
              <span className="insight-metric-label">
                {insight.metricLabel}
              </span>
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <div className="insights-widget-content">
      <div className="widget-header">
        <h3>Insights</h3>
        <span className="view-all-link">View All</span>
      </div>

      <div className="insights-list">
        {renderContent()}
      </div>
    </div>
  );
}